import Backdrop from "@mui/material/Backdrop";
import Fade from "@mui/material/Fade";
import Modal from "@mui/material/Modal";
import React, { useEffect } from "react";
import { Col, Row } from "react-bootstrap";
import { useDispatch } from "react-redux";
import templateProgress from "../../Assets/Images/templateProgress.webp";
import downloadLottie from "../../Assets/Animations/saveTemplateDone.json";
import { useTranslation } from "react-i18next";
import "../../Assets/Styles/pages/uploadImage.scss";
import { ClearImage } from "../../Redux/MainImage/imageSlice";
import { deleteAll } from "../../Redux/WaterMarks/watermarks";
import LottiePlayer from "../LottiePlayer/lottiePlayer";

interface TemplateResponseProps {
  open: boolean;
  setOpen: (v: boolean) => void;
  /**
   * EWW-409. Keep the photo and its watermarks on the canvas after the save confirmation
   * instead of wiping them.
   *
   * `ClearImage()` resets the whole mainImage slice, which empties `mainImage.url`, and the
   * editor redirects away whenever that is empty (see the guard in useEditLogic). That is why
   * saving a template from a normal editing session ejected the user off the canvas even though
   * they still had a watermarked photo they might want to export.
   *
   * Defaults to false so the template authoring flows (Create Template, updating an existing
   * one) keep clearing -- there is nothing left to do on the canvas once the template is saved,
   * and that clear is what triggers the redirect back to the template section (EWW-456).
   *
   * Only Header.tsx passes it, computed per-save (see `keepCanvasOnSaveDone`'s own comment for
   * why it can't just read a Redux flag live at this point).
   */
  keepImageOnDone?: boolean;
}

const TemplateResponse = ({
  open,
  setOpen,
  keepImageOnDone = false,
}: TemplateResponseProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  useEffect(() => {
    if (open) {
      const timeoutId = setTimeout(() => {
        if (!keepImageOnDone) {
          dispatch(ClearImage());
          dispatch(deleteAll());
        }
        setOpen(false);
      }, 2000);

      return () => clearTimeout(timeoutId); // Clean up the timeout if the component unmounts or if `open` changes before the timeout completes
    }
  }, [open, keepImageOnDone]);

  return (
    <Modal
      open={open}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
      closeAfterTransition
      BackdropComponent={Backdrop}
      BackdropProps={{
        timeout: 800,
        style: {
          backdropFilter: "blur(25px)",
          backgroundColor: "rgba(0, 0, 0, 0.7)",
        },
      }}
    >
      <Fade in={open}>
        <Row className="model downloadProgressModal p-0 m-0">
          <Col className="d-flex flex-column justify-content-center align-items-center p-2 pb-0">
            <div className="position-relative w-100 p-1 pb-0">
              <img
                alt="progress"
                src={templateProgress}
                className="w-100 rounded roundedBottom_0"
              />
              <div className="downloadAnimation templateDoneAnimation">
                <LottiePlayer src={downloadLottie} />
              </div>
            </div>
            <Row className="productSansBold mt-5 text_23 mb-1">
              {t("SavingTemplateDone")}
            </Row>
            <Row className="productSansLight text_17 mb-5">
              {t("SavingTemplateDescriptionDone")}
            </Row>
          </Col>
        </Row>
      </Fade>
    </Modal>
  );
};

export default TemplateResponse;
